import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { getGradeDetailsByIdsQuery } from "../modules/GradesQueries";
import { DEBOUNCE_MS } from "../modules/GradesConstants";
import _ from "lodash";

/**
 * Phase 2 hook: Fetches grade name + participant counts for visible grade IDs.
 * Unlike Courses, Grade type has staffCount/studentCount/familyMemberCount directly,
 * so a single query fetches both name and counts together.
 *
 * Uses debouncing (300ms) and ref-based caching to avoid re-fetching already-loaded grades.
 * When fetchAllIds is provided (non-empty), fetches all IDs at once (used during search).
 *
 * @param {string[]} visibleGradeIds - IDs of grades currently in the viewport
 * @param {boolean} viewOnly - If true, skips fetching
 * @param {string[]} [fetchAllIds] - When non-empty, fetch these IDs instead of visible ones (for search)
 * @returns {{ gradeDetailsMap: Object, loading: boolean }}
 */
const useGradeDetails = (visibleGradeIds, viewOnly, fetchAllIds) => {
  const gradeDetailsMapRef = useRef({});
  const previousGradeIdsRef = useRef([]);
  const [debouncedVisibleIds, setDebouncedVisibleIds] = useState(
    visibleGradeIds
  );
  const [preLoading, setPreLoading] = useState(false);
  const [detailsVersion, setDetailsVersion] = useState(0);

  // When fetchAllIds is provided, use those directly (no debounce needed for search)
  const isFetchAll = !_.isEmpty(fetchAllIds);

  // Debounce visible IDs to avoid excessive queries during scrolling
  useEffect(() => {
    if (isFetchAll) return; // Skip debounce when fetching all
    setPreLoading(true);
    const timeoutId = setTimeout(() => {
      setPreLoading(false);
      setDebouncedVisibleIds(visibleGradeIds);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [JSON.stringify(visibleGradeIds), isFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter out already-cached IDs to avoid re-fetching
  const gradeIdsToFetch = useMemo(() => {
    const sourceIds = isFetchAll ? fetchAllIds : debouncedVisibleIds;
    if (_.isEmpty(sourceIds)) return [];

    const idsToFetch = _.filter(
      sourceIds,
      id => !gradeDetailsMapRef.current[id]
    );

    const sortedIds = _.sortBy(idsToFetch);

    if (_.isEqual(sortedIds, previousGradeIdsRef.current)) {
      return previousGradeIdsRef.current;
    }

    previousGradeIdsRef.current = sortedIds;
    return sortedIds;
  }, [debouncedVisibleIds, isFetchAll, fetchAllIds]);

  const { data, loading } = useQuery(getGradeDetailsByIdsQuery, {
    variables: { gradeIds: gradeIdsToFetch },
    context: { useHttpLink: true },
    skip: _.isEmpty(gradeIdsToFetch) || viewOnly,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
  });

  // Merge fetched details into the persistent ref
  useEffect(() => {
    const multiNodeData = _.get(data, "multiNode", []);

    if (_.isEmpty(multiNodeData)) return;

    let hasNewData = false;
    _.forEach(multiNodeData, grade => {
      if (grade.id && !gradeDetailsMapRef.current[grade.id]) {
        gradeDetailsMapRef.current[grade.id] = grade;
        hasNewData = true;
      }
    });

    if (hasNewData) {
      setDetailsVersion(prev => prev + 1);
    }
  }, [data]);

  // Return a snapshot of the ref for consumers
  const gradeDetailsMap = useMemo(() => ({ ...gradeDetailsMapRef.current }), [
    detailsVersion,
  ]);

  return {
    gradeDetailsMap,
    loading: (loading && _.isEmpty(data)) || preLoading,
  };
};

export default useGradeDetails;
